import SegmentedTab from 'components/partials/segmented-tab/segmented-tab';
import { useFeatureHintTarget } from 'components/shared/feature-hints';

import CriticalElementRow from 'containers/user/critical-elements-monitoring/components/critical-element-row';
import CriticalElementsRosterSkeleton from 'containers/user/critical-elements-monitoring/components/critical-elements-roster-skeleton';
import type { StageCardDisplayMode } from 'containers/user/critical-elements-monitoring/components/funnel-stage-card';
import { getStageVolume } from 'containers/user/critical-elements-monitoring/helpers/get-stage-volume';
import type { ProspectAvailability } from 'containers/user/critical-elements-monitoring/helpers/get-prospect-availability';
import { MONITORING_HINT_ID } from 'containers/user/critical-elements-monitoring/walkthrough/monitoring-hints.config';
import type {
  CompareTo,
  CriticalElementMonitoringMap,
  CriticalElementSummary,
  ExtendWithOption,
} from 'containers/user/critical-elements-monitoring/critical-elements-monitoring.types';

export interface CriticalElementsRosterProps {
  criticalElements: CriticalElementSummary[];
  metrics: CriticalElementMonitoringMap | undefined;
  isMetricsLoading: boolean;
  isRosterLoading: boolean;
  compareTo: CompareTo;
  displayMode: StageCardDisplayMode;
  onDisplayModeChange: (displayMode: StageCardDisplayMode) => void;
  selectedUnentitledExtensions: readonly ExtendWithOption[];
  skeletonCardCount: number;
  prospectAvailability: ProspectAvailability;
  isContentLoaded: boolean;
  isAnalyticsEnabled?: boolean;
}

const CriticalElementsRoster = ({
  criticalElements,
  metrics,
  isMetricsLoading,
  isRosterLoading,
  compareTo,
  displayMode,
  onDisplayModeChange,
  selectedUnentitledExtensions,
  skeletonCardCount,
  prospectAvailability,
  isContentLoaded,
  isAnalyticsEnabled = true,
}: CriticalElementsRosterProps) => {
  const isRosterEmpty = !isRosterLoading && !criticalElements.length;
  const isVolumeOnly = compareTo === 'prior-year';
  const criticalElementDisplayModeHint = useFeatureHintTarget(
    MONITORING_HINT_ID.criticalElementDisplayMode
  );
  const headerHintTargetProps = useFeatureHintTarget(
    MONITORING_HINT_ID.criticalElementRow
  );

  return (
    <section
      className="flex flex-col gap-2 pt-4"
      data-testid="critical-elements-roster"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-700">
          Critical Elements
        </h2>
        {isContentLoaded && !isRosterEmpty && (
          <SegmentedTab
            {...criticalElementDisplayModeHint}
            label="Critical Element display mode"
            value={displayMode}
            onChange={onDisplayModeChange}
            options={[
              { value: 'volume', label: 'Volume' },
              { value: 'rate', label: 'Rate', disabled: isVolumeOnly },
              {
                value: 'proportion',
                label: 'Proportion',
                disabled: isVolumeOnly,
              },
            ]}
          />
        )}
      </div>

      {isRosterLoading && <CriticalElementsRosterSkeleton />}

      {isRosterEmpty && (
        <p className="text-sm text-gray-500">
          No critical elements to display.
        </p>
      )}

      {!isRosterLoading && !!criticalElements.length && (
        <div role="list" className="flex flex-col gap-2">
          {criticalElements.map((summary, index) => (
            <CriticalElementRow
              key={summary.id}
              summary={summary}
              funnel={metrics?.byCriticalElementId[summary.id]}
              isFunnelLoading={isMetricsLoading}
              compareTo={compareTo}
              displayMode={displayMode}
              selectedUnentitledExtensions={selectedUnentitledExtensions}
              skeletonCardCount={skeletonCardCount}
              prospectAvailability={prospectAvailability}
              isAnalyticsEnabled={isAnalyticsEnabled}
              headerHintTargetProps={
                index === 0 ? headerHintTargetProps : undefined
              }
              isMonitoringDataAbsent={
                !!metrics && !metrics.byCriticalElementId[summary.id]
              }
              totalInquiryVolume={getStageVolume(metrics?.total, 'inquiry')}
              totalApplicationVolume={getStageVolume(
                metrics?.total,
                'application'
              )}
              totalNetDepositVolume={getStageVolume(
                metrics?.total,
                'netDeposit'
              )}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default CriticalElementsRoster;
